import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Center,
  Flex,
  Stack,
  Text,
  Link,
  Wrap,
  Tooltip,
  WrapItem,
  useColorModeValue,
  useDisclosure,
  useColorMode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from 'remark-gfm'
import Clipboard from "./Clipboard";
import CopyButton from "./CopyButton";

const textLimit = 1000;

const Messages = ({ messages, shouldScroll, topics, sources, title }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalSource, setModalSource] = useState({});

  const AlwaysScrollToBottom = () => {
    const elementRef = useRef();
    useEffect(() => {
      if (shouldScroll) {
        elementRef.current.scrollIntoView();
      }
    }, [messages]);
    return <div ref={elementRef} />;
  };

  const openModal = (source) => {
    setModalSource(source);
    onOpen();
  }

  const preStyle = {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    width: '100%',
  };

  const assistantTheme = {
    bg: useColorModeValue("gray.100", "gray.600"),
    topicBG: useColorModeValue("#ffc3cc", "#ff536d"),
    color: useColorModeValue("black", "white"),
    code: colorMode === "light" ? oneDark : oneDark,
  };

  const defaultTopics = topics ? topics : [
    'How do investors use technical analysis to invest?',
    'Write a business plan for a vending machine startup.',
    'Draft a document on how to build a bookshelf',
    'Any birthday party ideas for my 5 year old?',
  ];

  const transformText = (inputText) => {
    const regex = /s\.\s(\d+)\.(\d+)/g;

    return inputText.replace(regex, (_, statuteSection, statuteNumber) => {
      const paddedStatuteSection = statuteSection.padStart(4, "0");
      const url = `https://www.flsenate.gov/Laws/Statutes/2022/${paddedStatuteSection}.${statuteNumber}`;
      return `<a href="${url}" style="color:blue" target="_blank" rel="noreferrer">s. ${paddedStatuteSection}.${statuteNumber}</a>`;
    });
  };

  return (
    <Flex w="100%" h="100%" justify="center" overflowY="auto">
      <Flex w="80%" flexDirection="column">
        <Center w="100%" marginBottom="20px">
          <Stack width="80%">
            <h1 className="mt-5 text-center font-bold text-3xl md:text-6xl lg:text-7xl">
              Ask <span style={{ color: "#ff415e" }}>{title !== undefined ? <>{title.length > 3 ? <br></br> : <></>}{title}</> : <>Robin</>}</span>
            </h1>
            <br></br>
            <p className="text-lg text-center font-bold">
              Every Batman needs a Robin!
            </p>
            <Text as="i" textAlign="center" fontSize="xl">
              Ask me anything!
            </Text>
            <br></br>
            <Text as="b" align="center">
              {
                defaultTopics && defaultTopics.length > 0 && (
                  <>
                    Examples
                  </>
                )
              }
            </Text>
            <br></br>
            <Box margin="auto !important">
              <Wrap textAlign="center" alignItems="center" justify="center">
                {defaultTopics.map((topic, index) => (
                  <WrapItem key={index} marginY=".5rem">
                    <Box
                      padding=".55rem"
                      borderRadius="10px"
                      backgroundColor={assistantTheme.topicBG}
                      textAlign="center"
                      maxWidth="15rem"
                    >
                      <Text fontSize={["xs", "md"]} fontWeight="500">
                        {topic}
                      </Text>
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          </Stack>
        </Center>
        {messages.map((item, index) => {
          if (item.role === "user") {
            return (
              <Flex key={index} w="100%" justify="flex-start">
                <Flex
                  bg="#1982FC"
                  color="white"
                  minW="40px"
                  maxW="75%"
                  borderRadius={"15px"}
                  my="1"
                  p="3"
                >
                  <Text width="100%" whiteSpace="pre-wrap">
                    {item.content}
                  </Text>
                </Flex>
              </Flex>
            );
          } else {
            return (
              <Flex key={index}>
                <HStack marginY="auto" marginX="0" maxWidth="100%">
                  <Flex
                    color={assistantTheme.color}
                    minW="40px"
                    overflowX="auto"
                    borderRadius={"15px"}
                    my="1"
                    p="3"
                  >
                    <Stack width="100%">
                      <div
                        className="prose"
                        style={{ color: assistantTheme.color, maxWidth: "100%" }}
                      >
                        <ReactMarkdown
                          plugins={[remarkGfm]}
                          components={{
                            code({
                              node,
                              inline,
                              className,
                              children,
                              ...props
                            }) {
                              const match = /language-(\w+)/.exec(
                                className || ""
                              );
                              return !inline && match ? (
                                <Box
                                  className="syntax-highlighter-wrapper"
                                  position="relative"
                                >
                                  <SyntaxHighlighter
                                    children={String(children).replace(
                                      /\n$/,
                                      ""
                                    )}
                                    style={{ ...assistantTheme.code }}
                                    language={match[1]}
                                    PreTag="div"
                                    {...props}
                                  />
                                  <Clipboard
                                    value={String(children).replace(/\n$/, "")}
                                  />
                                </Box>
                              ) : (
                                <Box>
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                  {String(children).replace(/\n$/, "").length >
                                    100 && (
                                      <CopyButton
                                        value={String(children).replace(
                                          /\n$/,
                                          ""
                                        )}
                                      />
                                    )}
                                </Box>
                              );
                            },
                          }}
                        >
                          {item.content}
                        </ReactMarkdown>
                      </div>
                      <div className="flex items-center m-auto !important">
                        {item.content.length > textLimit && (!sources || sources.length === 0) && (
                          <CopyButton value={item.content} />
                        )}
                      </div>

                    </Stack>
                  </Flex>
                </HStack>
              </Flex>
            );
          }
        })}
        <div className=" justify-start items-start">
          {
            sources && sources.length > 0 && sources.map((source, index) => {
              const sourceText = source.book
                ? source.book
                : (source.chapter_title && source.chapter_name)
                  ? source.chapter_title + " - " + source.chapter_name
                  : "Source";

              return (
                <Tooltip
                  className={`${source?.section_number && source?.section_title ? 'inline' : 'hidden'}`}
                  label={"Section: " + source?.section_number + " - " + source?.section_title}
                  size="xl"
                  placement='right-start'
                >
                  <div
                    className={`${colorMode === 'light' ? 'p-2.5 btn btn-outline hover:bg-[#ff536d]' : 'text-white btn bg-[#ff536d] hover:bg-[#ff536d]'} m-1 shadow-sm transition duration-300 ease-in-out hover:scale-105`}
                    key={index}
                    mt={4}
                    onClick={() => openModal(source)}
                  >
                    {sourceText}
                  </div>
                </Tooltip>
              );
            })
          }
        </div>
        <Modal size="3xl" isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Source</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {
                (modalSource?.book || (modalSource.chapter_title + " - " + modalSource.chapter_name)) && modalSource?.content && (
                  <div className="mx-10">
                    <div className="text-lg font-bold">{modalSource?.book}</div>
                    {modalSource?.chapter && (<div className="text-md font-semibold">Chapter: {modalSource?.chapter}: {modalSource?.chapter_title}</div>)}
                    {modalSource?.page && (<div className="text-sm">page {modalSource?.page}</div>)}
                    <pre style={preStyle} className="prose" dangerouslySetInnerHTML={{ __html: transformText(modalSource?.content) }} />
                  </div>
                ) || <div>Sorry we had trouble finding this citation!</div>
              }

            </ModalBody>
            <ModalFooter>
              {modalSource?.direct_link || modalSource?.full_link ? (
                <Link target="_blank" textDecoration="none" href={modalSource?.direct_link || modalSource?.full_link} external>
                  <div
                    className={`mr-3 ${colorMode === 'light'
                      ? 'btn'
                      : 'text-white btn bg-[#ff536d] hover:bg-[#ff536d]'
                      }`}
                    onClick={onClose}
                  >
                    Visit {modalSource?.section_number}
                  </div>
                </Link>
              ) : null}
              <div
                className={`mr-3 ${colorMode === 'light'
                  ? 'btn'
                  : 'text-white btn bg-[#ff536d] hover:bg-[#ff536d]'
                  }`}
                onClick={onClose}
              >
                Close
              </div>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <AlwaysScrollToBottom />
      </Flex>
    </Flex >
  );
};

export default Messages;
